import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Calculator, CheckCircle2, Mail, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { projects } from '../mock';
import { PROJECT_DEMOS } from '../components/demos/ProjectDemos';
import { CaseStudySeo } from '../seo/PageSeo';
import { CASE_STUDIES, PROJECTS_SEGMENT, caseStudyBySlug, casePath, homePath } from '../seo/seoConfig';
import { RelatedLinks } from '../components/RelatedLinks';

export default function CaseStudyPage() {
  const { t } = useTranslation();
  const { lang, segment, slug } = useParams();
  const study = caseStudyBySlug(slug);
  const base = study && projects.find((p) => p.id === study.id);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!study || !base || segment !== PROJECTS_SEGMENT[lang]) return <Navigate to="/404" replace />;

  const project = { ...base, title: t(`projectData.${study.i18nKey}.title`), description: t(`projectData.${study.i18nKey}.description`) };
  const features = t(`caseStudies.${study.i18nKey}.features`, { returnObjects: true });
  const Demo = PROJECT_DEMOS[study.id];
  const others = CASE_STUDIES.filter((c) => c.id !== study.id);

  return (
    <main className="min-h-screen overflow-x-hidden bg-gradient-to-b from-gray-50 to-white dark:from-slate-950 dark:to-slate-900" data-testid={`case-study-${study.slug}`}>
      <CaseStudySeo lang={lang} study={study} project={project} />

      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Link to={`${homePath(lang)}#projects`} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 mb-6" data-testid="case-back-projects">
            <ArrowLeft className="h-3 w-3" /> {t('caseStudy.backProjects')}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-[2rem] leading-tight sm:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-testid="case-h1">{project.title}</h1>
              <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{project.description}</p>
              <div className="flex flex-wrap gap-2">
                {project.techStack.map((tech) => <Badge key={tech} variant="secondary">{tech}</Badge>)}
              </div>
            </div>
            <img src={project.image} alt={project.title} loading="lazy" className="w-full h-64 object-cover rounded-xl shadow-2xl border-2 border-gray-200 dark:border-gray-800" />
          </div>
        </div>
      </section>

      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-4">{t('caseStudy.featuresTitle')}</h2>
            <ul className="space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-8">
            <div>
              <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-4">{t('caseStudy.audienceTitle')}</h2>
              <p className="flex items-start gap-2 text-gray-700 dark:text-gray-300"><Users className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" /> {t(`caseStudies.${study.i18nKey}.audience`)}</p>
            </div>
            <div>
              <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-4">{t('caseStudy.techTitle')}</h2>
              <p className="text-gray-700 dark:text-gray-300">{project.techStack.join(' · ')}</p>
            </div>
          </div>
        </div>
      </section>

      {Demo && (
        <section className="py-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-2">{t('caseStudy.demoTitle')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('caseStudy.demoText')}</p>
            <div className="rounded-xl border-2 border-gray-200 dark:border-gray-800 bg-slate-950 p-4 sm:p-6 shadow-2xl overflow-hidden" data-testid="case-demo">
              <Demo />
            </div>
          </div>
        </section>
      )}

      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-6">{t('caseStudy.otherTitle')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {others.map((c) => (
              <Link key={c.slug} to={casePath(lang, c.slug)} data-testid={`case-link-${c.slug}`}>
                <Card className="h-full border-2 hover:border-blue-500/50 hover:shadow-lg transition-all duration-300">
                  <CardContent className="pt-5 flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{t(`projectData.${c.i18nKey}.title`)}</span>
                    <ArrowRight className="h-4 w-4 text-blue-600" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">{t('caseStudy.ctaTitle')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{t('caseStudy.ctaText')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <Link to={`${homePath(lang)}#calculator`} data-testid="case-cta-calculator"><Calculator className="h-5 w-5 mr-2" /> {t('caseStudy.ctaCalculator')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to={`${homePath(lang)}#contact`} data-testid="case-cta-contact"><Mail className="h-5 w-5 mr-2" /> {t('caseStudy.ctaContact')}</Link>
            </Button>
          </div>
        </div>
      </section>
      <RelatedLinks lang={lang} excludeCase={study.slug} />
    </main>
  );
}
